import { useState, useRef } from 'react'
import { Props_SnippetVideo } from './Video.props'
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import SnippetLink from '../Shared/Link/Link'

type VideoProvider = 'youtube' | 'vimeo' | 'video' | null

const getVideoMimeType = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase()
    switch (extension) {
        case 'mp4':
            return 'video/mp4'
        case 'webm':
            return 'video/webm'
        case 'ogg':
        case 'ogv':
            return 'video/ogg'
        case 'mov':
            return 'video/quicktime'
        default:
            return 'video/mp4'
    }
}

export const getVideoProvider = (url: string): VideoProvider => {
    if (!url) return null;
    if (url.includes('youtube') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('vimeo')) return 'vimeo';
    return 'video';
};

export const getEmbedUrl = (url: string, provider: VideoProvider): string => {
    if (provider === 'youtube') {
        let videoId = '';
        if (url.includes('youtu.be')) {
            videoId = url.split('/').pop()?.split('?')[0] || '';
        } else if (url.includes('youtube.com/watch')) {
            const params = new URLSearchParams(url.split('?')[1]);
            videoId = params.get('v') || '';
        } else if (url.includes('youtube.com/embed')) {
            const hasAutoplay = url.includes('autoplay=1');
            if (!hasAutoplay) {
                const separator = url.includes('?') ? '&' : '?';
                return `${url}${separator}autoplay=1`;
            }
            return url;
        }
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    
    const hasAutoplay = url.includes('autoplay=1');
    if (!hasAutoplay) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}autoplay=1`;
    }
    return url;
};


const SnippetVideo = (props: Props_SnippetVideo) => {
    const { snippet, heading, content, repeatable_link, video, linkProps } = props

    const [playing, setPlaying] = useState<boolean>(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)

    if (!video?.url?.value) return null;
    
    const videoProvider = getVideoProvider(video.url.value);
    const embedUrl = getEmbedUrl(video.url.value, videoProvider as VideoProvider);
    

    const buttonLabel = `{{js.snippet.video.button.play.label}}`
    const snippetVideoClass = 'snippet-video'

    const classSnippetThemeBackground = `snippet-video--theme-background snippet-video--theme-background-${snippet.themeBackground?.value}`
    const classSnippetContentThemeBackground = `snippet-video--theme-content-background snippet-video--theme-content-background-${snippet.themeContentBackground?.value}`
    const classHeadingVisibility = heading.visibility?.value ? `` : `${snippetVideoClass}__heading--hidden`
    const classHeadingAlignment: string = heading.alignment?.value ? `${snippetVideoClass}__heading--alignment-${heading.alignment.value}` : ``
    const classContentPosition: string = content.position?.value ? `${snippetVideoClass}--position-${content.position.value}` : ``
    const classContentAlignment: string = content.alignment?.value ? `${snippetVideoClass}--alignment-${content.alignment.value}` : ``
    const classContentVerticalAlignment: string = content.vertical_alignment?.value ? `${snippetVideoClass}--vertical-alignment-${content.vertical_alignment.value}` : ``
    const classVideoPlaying: string = playing ? `${snippetVideoClass}--playing` : ``
    const classVideoFullscreen: string = video.fullscreen?.value ? `${snippetVideoClass}--fullscreen` : ``
    const classLinkAlignment: string = linkProps.alignment?.value ? `${snippetVideoClass}__container-links--${linkProps.alignment.value}` : ``

    return (
        <section className={`${snippetVideoClass} ${classSnippetThemeBackground} ${classSnippetContentThemeBackground} ${classVideoPlaying} ${classVideoFullscreen} ${classContentPosition} ${classContentAlignment} ${classContentVerticalAlignment}`}>
            <div className={`${snippetVideoClass}__container`}>
                <div className={`${snippetVideoClass}__container-background`}>
                    <div className={`${snippetVideoClass}__container-content`}>
                        <div className={`${snippetVideoClass}__heading ${classHeadingVisibility} ${classHeadingAlignment}`}>{heading?.content?.value && CompileMarkdown(heading?.content?.value)}</div>

                       { content.content?.value && <div className={`${snippetVideoClass}__content`}>
                            {CompileMarkdown(content?.content?.value)}
                        </div>}
                        
                        <div className={`${snippetVideoClass}__container-links ${classLinkAlignment}`}>
                            { repeatable_link?.map((link, index) => (
                                <SnippetLink
                                    key={`link-${index}`}
                                    url={link.linkHref?.value ?? ''}
                                    className={`${snippetVideoClass}__link`}
                                    linkStyles={link.linkStyle?.value}
                                    target={link.linkTarget?.value}
                                >
                                    {link.linkContent?.value ?? ''}
                                </SnippetLink>
                            ))}
                        </div>
                    </div>
                    <div className={`${snippetVideoClass}__container-video-player`}>
                        { video?.thumbnail?.value && (
                            <figure className={`${snippetVideoClass}__container-thumbnail`}>
                                <img
                                    src={video.thumbnail.value}
                                    alt={video?.alt?.value || ''}
                                    className={`${snippetVideoClass}__thumbnail`}
                                    loading="lazy"
                                    width="540"
                                    height="540"
                                />
                            </figure>
                        )}
                        { video?.url?.value && (
                            <div className={`${snippetVideoClass}__container-video`}>
                                { video?.thumbnail?.value && (
                                    <button
                                        className={`${snippetVideoClass}__button-play`}
                                        aria-label={`{{js.snippet.video.button.play.aria-label}}`}
                                        onClick={() => setPlaying(true)}
                                    >
                                        {buttonLabel}
                                    </button>
                                )}
                                { playing && (
                                    <button
                                        className={`${snippetVideoClass}__button-close`}
                                        aria-label={`{{js.snippet.video.button.close.aria-label}}`}
                                        onClick={() => setPlaying(false)}
                                    >
                                        <span className={`${snippetVideoClass}__button-close-icon`}>×</span>
                                    </button>
                                )}
                                { embedUrl && playing && (videoProvider === 'youtube' || videoProvider === 'vimeo') && (
                                    <iframe
                                        className={`${snippetVideoClass}__video`}
                                        src={embedUrl}
                                        width="1280"
                                        height="720"
                                        title="Video player"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy={videoProvider === 'youtube' ? 'strict-origin-when-cross-origin' : undefined}
                                        allowFullScreen
                                    ></iframe>
                                )}
                                { playing && videoProvider === 'video' && (
                                    <video
                                        className={`${snippetVideoClass}__video`}
                                        controls
                                        controlsList='nodownload'
                                        width="1280"
                                        height="720"
                                        ref={videoRef}
                                        autoPlay
                                    >
                                        <track kind="captions" />
                                        <source src={video.url.value} type={getVideoMimeType(video.url.value)} />
                                        Your browser does not support the video tag.
                                    </video>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SnippetVideo